import { SelectorIcon, SortAscendingIcon, SortDescendingIcon } from "@heroicons/react/outline";
import { Tag } from "components/tag";
import { TooltipParagraph } from "components/tooltipParagraph";
import { EventStatusTypes, IOverviewEvents } from "interfaces"
import { get } from "lodash";
import { abbreviateNumbers, currencySymbol, formatNumber } from "utills/helpers";

interface EventsTableProps {
  data?: IOverviewEvents[];
  pageSize: number;
  currentPage: number;
  isLoading: boolean;
  _handleFilterChange: (key: string, value: string) => void;
  _toggleActive?: (id: string, isActive: boolean) => void;
  currentFilterValues: any;
}

export const EventsTable = ({data = [], isLoading, currentPage, pageSize, currentFilterValues, _handleFilterChange }: EventsTableProps) => {

  const getEventStatusText = (statusType: EventStatusTypes) => {
    switch (statusType) {
      case EventStatusTypes.CLOSED: return "Closed";
      case EventStatusTypes.COMING: return "Coming soon";
      case EventStatusTypes.SELLING: return "On Selling";
      default: return "";
    }
  }

  const _renderSortButton = (fieldKey: string) => {
    return get(currentFilterValues, "sort_by", "") !== fieldKey ? (
      <SelectorIcon className="size-4 inline-block hover:cursor-pointer" onClick={() => {
        _handleFilterChange("sort_by", fieldKey);
        _handleFilterChange("order", "desc");
      }} />
    ) : get(currentFilterValues, "order", "") === "" ? (
      <SelectorIcon className="size-4 inline-block hover:cursor-pointer" onClick={() => {
        _handleFilterChange("order", "desc");
      }} />
    ) : get(currentFilterValues, "order", "") === "desc" ? (
      <SortDescendingIcon className="size-4 inline-block hover:cursor-pointer" onClick={() => {
        _handleFilterChange("order", "asc");
      }} />
    ) : (
      <SortAscendingIcon className="size-4 inline-block hover:cursor-pointer" onClick={() => {
        _handleFilterChange("sort_by", "");
        _handleFilterChange("order", "");
      }} />
    )
  }

  return (
    <div className="flow-root">
      <div className="overflow-x-auto">
        <div className="relative inline-block min-w-full align-middle">
          {/* {data.length > 0 && isFetching ? (
            <div className="absolute w-full h-full top-0 left-0 bottom-0 right-0 bg-gray-900/50 flex items-center justify-center">
              <Loader />
            </div>
          ) : <></>} */}
          <table className="min-w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                >
                  No
                </th>
                <th scope="col" className="th">
                  Event name
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Gross revenue
                    {_renderSortButton("subtotal")}
                  </div>
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Orders
                    {_renderSortButton("transactions")}
                  </div>
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Items
                    {_renderSortButton("total_items")}
                  </div>
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Net revenue
                    {_renderSortButton("grand_total")}
                  </div>
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Discount
                    {_renderSortButton("discount_coupon")}
                  </div>
                </th>
                <th scope="col" className="th">
                  <div className="flex items-center gap-x-1">
                    Promotion
                    {_renderSortButton("discount_promotion")}
                  </div>
                </th>
                <th scope="col" className="th">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(!isLoading) ? data.map((item: IOverviewEvents, index: number) => (
                <tr key={index}>
                  <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                    {(currentPage - 1) * pageSize + 1 + index}
                  </td>
                  <td className="td max-w-40 overflow-x-auto">
                    <TooltipParagraph 
                      text={item.event_name}
                      className="whitespace-pre-wrap max-w-[280px]"
                    />
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.subtotal)}
                    >
                      {currencySymbol()} {abbreviateNumbers(item.subtotal)}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.transactions)}
                    >
                      {abbreviateNumbers(item.transactions)}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.total_items)}
                    >
                      {abbreviateNumbers(item.total_items)}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.grand_total)}
                    >
                      {currencySymbol()} {abbreviateNumbers(item.grand_total)}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.discount_coupon)}
                    >
                      {currencySymbol()} {abbreviateNumbers(item.discount_coupon)}
                    </span>
                  </td>
                  <td className="td">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-content={formatNumber(item.discount_promotion)}
                    >
                      {currencySymbol()} {abbreviateNumbers(item.discount_promotion)}
                    </span>
                  </td>
                  <td className="td">
                    <Tag text={getEventStatusText(item.selling_type)} type={item.selling_type}  />
                  </td>
                </tr>
              )) : Array(10).fill(null).map((item: any, index: number) => (
                <tr key={index} className="h-[57px]">
                  <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td max-w-40 overflow-x-auto">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                  <td className="td">
                    <div className="h-3 bg-gray-200 rounded-full w-20 max-w-full" />
                  </td>
                </tr>
              ))}
              {data.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={10} className="td">
                    <div className="text-center">
                      No data
                    </div>
                  </td>
                </tr>
              ) : <></>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}